import { RootState } from "@/redux/store";
import { fetchBaseQuery } from "@reduxjs/toolkit/query";
import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { logout, tokenReceived, logoutAsync } from "@/redux/authSlice";
import { setCode, setCounter } from "@/redux/counterSlice";
import { toast } from "@/components/ui/use-toast";

let hasShownNetworkError = false; // add this flag at the top of your module

const API_BASE_URL = import.meta.env.VITE_API_URL;
interface FastApiError {
  detail?: string | string[]; // FastAPI's detail field can be a string or an array of strings
  message?: string | string[]; // FastAPI's detail field can be a string or an array of strings
}
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (
    result.error &&
    !navigator.onLine // specifically check if user is offline
  ) {
    if (!hasShownNetworkError) {
      toast({
        variant: "destructive",
        title: "Network Error",
        description:
          "No internet connection. Please check your connection and try again.",
      });
      hasShownNetworkError = true;

      // Reset the flag after 5 seconds so it can show again if needed later
      setTimeout(() => {
        hasShownNetworkError = false;
      }, 5000);
    }
    return result;
  }

  // refresh token
  const skipLogoutOn401 = Boolean((extraOptions as any)?.skipLogoutOn401);
  if (result.error && result.error.status === 401) {
    if (skipLogoutOn401) {
      // For requests that opt-out (e.g., mobile payment deeplink flows),
      // do not force logout on 401
      return result;
    }
    api.dispatch(setCode(null));
    api.dispatch(setCounter(null));
    api.dispatch(logoutAsync());

    toast({
      variant: "destructive",
      title: "Session Expired",
      description: "Your session has expired. Please log in again to continue.",
      style: { width: "350px" }, // Set the desired width here
    });
  }

  if (result.error) {
    const { status, data } = result.error as FetchBaseQueryError & {
      data?: FastApiError;
    };

    // Check for a 422 Unprocessable Entity or other specific errors
    if (status === 422 && data && typeof data === "object") {
      const errorDetail = Array.isArray(data.detail)
        ? data.detail.join(", ")
        : data.detail || "Unprocessable entity";
      result.error.data = { message: errorDetail };
    } else {
      // For other errors, keep the default behavior
      result.error.data = {
        message:
          (data as FastApiError)?.detail || "An unexpected error occurred",
      };
    }
  }
  return result;
};

const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.userToken;
    // const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (token) headers.set("Authorization", `Bearer ${token}`);
    headers.set("Access-Control-Allow-Origin", `*`);
    headers.set("Accept", "application/json");
    // headers.set("X-Timezone", timezone);

    return headers;
  },
});

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
  tagTypes: [
    "Common",
    "Credits",
    "Facility",
    "FacilityCategory",
    "SalesTax",
    "IncomeCategory",
    "Memberships",
    "Groups",
    "Roles",
    "Coaches",
    "Staffs",
    "Exercise",
    "Foods",
    "MealPlans",
    "Members",
    "Reset",
    "Workout",
    "Counter",
    "Register",
    "Transaction",
    "Organization",
    "Hardware",
    "Extensions",
    "User",
    "Invoice",
    "Product",
    "SysConfig",
    "Wallet",
    "RefundWallet",
    "CheckIn",
    "MemberGroup",
    "PaymentGateway",
    "BankPayment",
    "Events",
    "DHAccounting",
    "Consultation",
    "BusinessUnit",
    "ProductCategory",
    "CategoriesPage",
  ],
});
